import { useState, useEffect, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  Palette,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Pencil,
  Trash2,
  MoreHorizontal,
  Upload,
  FileUp,
} from 'lucide-react'
import { BrandKit, BrandKitFormData, BrandKitStatus } from '../../types/brandKit'
import { brandKitApi, PaginationMeta } from '../../lib/api/brandKits'
import { CreateBrandKitModal } from './CreateBrandKitModal'
import { ExtractBrandKitModal } from './ExtractBrandKitModal'

/* ------------------------------------------------------------------ */
/*  Status badge                                                       */
/* ------------------------------------------------------------------ */
const STATUS_STYLES: Record<BrandKitStatus, string> = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  draft: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  archived: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
}

function StatusBadge({ status }: { status: BrandKitStatus }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${
        STATUS_STYLES[status] || STATUS_STYLES.draft
      }`}
    >
      {status}
    </span>
  )
}

/* ------------------------------------------------------------------ */
/*  BrandKitList component                                             */
/* ------------------------------------------------------------------ */
export function BrandKitList() {
  const [searchParams, setSearchParams] = useSearchParams()

  const initialPage = parseInt(searchParams.get('page') || '1', 10)
  const initialQuery = searchParams.get('q') || ''

  const [brandKits, setBrandKits] = useState<BrandKit[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState(initialQuery)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isExtractModalOpen, setIsExtractModalOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)

  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null)
  const actionMenuRef = useRef<HTMLDivElement>(null)

  const [brandKitToDelete, setBrandKitToDelete] = useState<BrandKit | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const [brandKitToEdit, setBrandKitToEdit] = useState<BrandKit | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)

  const [pagination, setPagination] = useState<PaginationMeta>({
    current_page: initialPage,
    total_pages: 1,
    total_count: 0,
  })

  const searchTimerRef = useRef<NodeJS.Timeout | null>(null)
  const hasLoadedRef = useRef(false)

  /* ---- URL sync ---- */
  const updateUrlParams = useCallback(
    (page: number, query: string) => {
      const params = new URLSearchParams()
      if (page > 1) params.set('page', page.toString())
      if (query) params.set('q', query)
      setSearchParams(params, { replace: true })
    },
    [setSearchParams]
  )

  /* ---- Load brand kits ---- */
  const loadBrandKits = useCallback(
    async (page = 1, query = '') => {
      setIsLoading(true)
      try {
        const { brand_kits: data, meta } = await brandKitApi.getBrandKits({
          page,
          q: query || undefined,
        })
        setBrandKits(data)
        setPagination(meta)
        updateUrlParams(page, query)
      } catch (error) {
        console.error('Failed to load brand kits:', error)
        toast.error('Failed to load brand kits')
      } finally {
        setIsLoading(false)
      }
    },
    [updateUrlParams]
  )

  useEffect(() => {
    if (hasLoadedRef.current) return
    hasLoadedRef.current = true
    loadBrandKits(initialPage, initialQuery)
  }, [loadBrandKits, initialPage, initialQuery])

  /* ---- Click-outside for action menu ---- */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target as Node)) {
        setOpenActionMenu(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  /* ---- Search ---- */
  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => loadBrandKits(1, value), 300)
  }

  useEffect(
    () => () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    },
    []
  )

  /* ---- Pagination ---- */
  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > pagination.total_pages) return
    loadBrandKits(newPage, searchQuery)
  }

  /* ---- Create ---- */
  const handleCreate = async (data: BrandKitFormData) => {
    setIsCreating(true)
    try {
      const created = await brandKitApi.createBrandKit(data)
      setBrandKits((prev) => [created, ...prev])
      setPagination((prev) => ({ ...prev, total_count: prev.total_count + 1 }))
      toast.success('Brand kit created successfully')
      setIsCreateModalOpen(false)
    } catch (error) {
      console.error('Failed to create brand kit:', error)
      toast.error('Failed to create brand kit')
      throw error
    } finally {
      setIsCreating(false)
    }
  }

  /* ---- Edit ---- */
  const handleEditClick = async (bk: BrandKit) => {
    setOpenActionMenu(null)
    try {
      const full = await brandKitApi.getBrandKit(bk.id)
      setBrandKitToEdit(full)
    } catch {
      toast.error('Failed to load brand kit details')
    }
  }

  const handleUpdate = async (data: BrandKitFormData) => {
    if (!brandKitToEdit) return
    setIsUpdating(true)
    try {
      const updated = await brandKitApi.updateBrandKit(brandKitToEdit.id, data)
      setBrandKits((prev) => prev.map((b) => (b.id === updated.id ? updated : b)))
      toast.success('Brand kit updated successfully')
      setBrandKitToEdit(null)
    } catch (error) {
      console.error('Failed to update brand kit:', error)
      toast.error('Failed to update brand kit')
      throw error
    } finally {
      setIsUpdating(false)
    }
  }

  /* ---- Delete ---- */
  const handleDeleteClick = (bk: BrandKit) => {
    setBrandKitToDelete(bk)
    setOpenActionMenu(null)
  }

  const confirmDelete = async () => {
    if (!brandKitToDelete) return
    setIsDeleting(true)
    try {
      await brandKitApi.deleteBrandKit(brandKitToDelete.id)
      setBrandKits((prev) => prev.filter((b) => b.id !== brandKitToDelete.id))
      setPagination((prev) => ({ ...prev, total_count: Math.max(0, prev.total_count - 1) }))
      toast.success('Brand kit deleted successfully')
      setBrandKitToDelete(null)
    } catch (error) {
      console.error('Failed to delete brand kit:', error)
      toast.error('Failed to delete brand kit')
    } finally {
      setIsDeleting(false)
    }
  }

  /* ---- Extraction complete callback ---- */
  const handleExtractionComplete = (brandKit: BrandKit) => {
    setBrandKits((prev) => {
      const exists = prev.find((b) => b.id === brandKit.id)
      if (exists) {
        return prev.map((b) => (b.id === brandKit.id ? brandKit : b))
      }
      return [brandKit, ...prev]
    })
    setPagination((prev) => ({ ...prev, total_count: prev.total_count + 1 }))
  }

  /* ---- Helpers ---- */
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const formatFullDateTime = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  /* ---- Pagination helpers ---- */
  const getPageNumbers = (): (number | 'ellipsis')[] => {
    const { current_page, total_pages } = pagination
    const pages: (number | 'ellipsis')[] = []
    if (total_pages <= 7) {
      for (let i = 1; i <= total_pages; i++) pages.push(i)
    } else {
      pages.push(1)
      if (current_page > 3) pages.push('ellipsis')
      const start = Math.max(2, current_page - 1)
      const end = Math.min(total_pages - 1, current_page + 1)
      for (let i = start; i <= end; i++) pages.push(i)
      if (current_page < total_pages - 2) pages.push('ellipsis')
      pages.push(total_pages)
    }
    return pages
  }

  const renderPagination = () => {
    if (pagination.total_pages <= 1) return null
    const pageNumbers = getPageNumbers()
    const perPage = 20
    const startItem = (pagination.current_page - 1) * perPage + 1
    const endItem = Math.min(pagination.current_page * perPage, pagination.total_count)

    return (
      <div className="flex items-center justify-between py-4 px-6">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Showing <span className="font-medium text-gray-900 dark:text-white">{startItem}</span> to{' '}
          <span className="font-medium text-gray-900 dark:text-white">{endItem}</span> of{' '}
          <span className="font-medium text-gray-900 dark:text-white">{pagination.total_count}</span>{' '}
          results
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => handlePageChange(1)}
            disabled={pagination.current_page <= 1 || isLoading}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="First page"
            title="First page"
          >
            <ChevronsLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </button>
          <button
            onClick={() => handlePageChange(pagination.current_page - 1)}
            disabled={pagination.current_page <= 1 || isLoading}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Previous page"
            title="Previous page"
          >
            <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </button>
          <div className="flex items-center gap-1 mx-1">
            {pageNumbers.map((page, index) =>
              page === 'ellipsis' ? (
                <span key={`ellipsis-${index}`} className="px-2 text-gray-400 dark:text-gray-500">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  disabled={isLoading}
                  className={`min-w-[32px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${
                    pagination.current_page === page
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  {page}
                </button>
              )
            )}
          </div>
          <button
            onClick={() => handlePageChange(pagination.current_page + 1)}
            disabled={pagination.current_page >= pagination.total_pages || isLoading}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Next page"
            title="Next page"
          >
            <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </button>
          <button
            onClick={() => handlePageChange(pagination.total_pages)}
            disabled={pagination.current_page >= pagination.total_pages || isLoading}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Last page"
            title="Last page"
          >
            <ChevronsRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      </div>
    )
  }

  /* ================================================================ */
  /*  Render                                                           */
  /* ================================================================ */
  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-gray-950">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-800">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-medium text-gray-900 dark:text-white">Brand Kits</h1>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsExtractModalOpen(true)}
                className="flex items-center gap-2 px-4 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg transition-colors text-sm font-medium"
              >
                <FileUp className="w-4 h-4" />
                Extract from File
              </button>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center gap-2 px-4 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                New Brand Kit
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-6">
          {/* Search */}
          <div className="sticky top-0 z-10 bg-white dark:bg-gray-950 py-3 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center justify-end">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search brand kits..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-64 px-3 py-1.5 pl-9 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 text-sm text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-600 focus:border-gray-300 dark:focus:border-gray-600 transition-colors"
                />
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-slate-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          {isLoading && brandKits.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto mb-3"></div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading brand kits...</p>
              </div>
            </div>
          ) : brandKits.length === 0 ? (
            <div className="text-center py-16">
              <Palette className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400 mb-1">
                {searchQuery ? 'No brand kits match your search' : 'No brand kits yet'}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mb-4">
                {searchQuery
                  ? 'Try adjusting your search terms'
                  : 'Create a brand kit manually or extract from a file'}
              </p>
              {!searchQuery && (
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => setIsExtractModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg transition-colors text-sm font-medium"
                  >
                    <Upload className="w-4 h-4" />
                    Extract from File
                  </button>
                  <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors text-sm font-medium"
                  >
                    Create Manually
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Table Header */}
              <div className="sticky top-[53px] z-10 grid grid-cols-12 gap-4 px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-gray-800/50 bg-white dark:bg-gray-950">
                <div className="col-span-1">ID</div>
                <div className="col-span-4">Name</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Advertisers</div>
                <div className="col-span-2">Created</div>
                <div className="col-span-1 text-right">Actions</div>
              </div>

              {/* Rows */}
              <div className="divide-y divide-gray-100 dark:divide-gray-800/50">
                {brandKits.map((bk) => (
                  <div
                    key={bk.id}
                    className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors group"
                  >
                    <div className="col-span-1">
                      <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                        {bk.id}
                      </span>
                    </div>
                    <div className="col-span-4 min-w-0">
                      <span
                        className="text-sm font-medium text-gray-900 dark:text-white truncate block"
                        title={bk.name}
                      >
                        {bk.name}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <StatusBadge status={bk.status} />
                    </div>
                    <div className="col-span-2 min-w-0">
                      {bk.advertisers && bk.advertisers.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {bk.advertisers.map((adv) => (
                            <span
                              key={adv.id}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                            >
                              {adv.name}
                            </span>
                          ))}
                        </div>
                      ) : bk.advertiser_ids?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {bk.advertiser_ids.map((id) => (
                            <span
                              key={id}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                            >
                              {id}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">All</span>
                      )}
                    </div>
                    <div className="col-span-2">
                      <span
                        className="text-sm text-gray-600 dark:text-gray-400 cursor-default"
                        title={formatFullDateTime(bk.created_at)}
                      >
                        {formatDate(bk.created_at)}
                      </span>
                    </div>
                    <div
                      className="col-span-1 flex justify-end relative"
                      ref={openActionMenu === bk.id ? actionMenuRef : null}
                    >
                      <button
                        onClick={() =>
                          setOpenActionMenu(openActionMenu === bk.id ? null : bk.id)
                        }
                        className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                        aria-label="Brand kit actions"
                      >
                        <MoreHorizontal className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      </button>
                      {openActionMenu === bk.id && (
                        <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20">
                          <button
                            onClick={() => handleEditClick(bk)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                          >
                            <Pencil className="h-4 w-4" />
                            Edit
                          </button>
                          <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                          <button
                            onClick={() => handleDeleteClick(bk)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex-shrink-0 bg-white dark:bg-gray-950 border-t border-gray-200 dark:border-gray-800">
          {renderPagination()}
        </div>
      )}

      {/* Create Modal */}
      <CreateBrandKitModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreate}
        isLoading={isCreating}
      />

      {/* Edit Modal */}
      <CreateBrandKitModal
        isOpen={!!brandKitToEdit}
        onClose={() => setBrandKitToEdit(null)}
        onSubmit={handleUpdate}
        isLoading={isUpdating}
        brandKit={brandKitToEdit}
      />

      {/* Extract from File Modal */}
      <ExtractBrandKitModal
        isOpen={isExtractModalOpen}
        onClose={() => setIsExtractModalOpen(false)}
        onComplete={handleExtractionComplete}
      />

      {/* Delete Confirmation */}
      {brandKitToDelete && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !isDeleting && setBrandKitToDelete(null)}
          />
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Delete Brand Kit
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  This action cannot be undone
                </p>
              </div>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                "{brandKitToDelete.name}"
              </span>
              ?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setBrandKitToDelete(null)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
